import { Grid, Box, FormControl } from "@mui/material";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, PickersDay } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { memo, useEffect, useRef, useState } from "react";
import { IFormControlProperty } from "@definitions/index";
import { FieldError } from "@components/FieldError";
import { flexCenterProps, paddingX1Props } from "@styles/global-css-class";
import { PickersDayProps } from "@mui/lab";
import DiffInheritanceIndicator from "@components/DiffInheritanceIndicator";

function NewHighlightedDatePickerFormControl({
	childKey,
	label,
	name,
	value,
	defaultValue,
	onChange,
	isRequired,
	isDisabled,
	isHidden,
	formState,
	xsFormControl = 12,
	mdFormControl = 12,
	xsLabel = 6,
	xsInput,
	textAlign,
	textErrorAlign,
	itemSx,
	textSx,
	zIndex,
	isExpanded,
	highlightedDays,
	errorName,
	showIndicator,
	isIndicatorTakeSpace,
	...props
}: Readonly<IFormControlProperty>) {
	const [dateValue, setDateValue] = useState<Dayjs | null>(null);
	const [cleared, setCleared] = useState<boolean>(false);

	const handleValue = (dateValue: Dayjs | null) => {
		if (dateValue === null || dateValue.toString().startsWith('0001-01-01')) {
			return defaultValue ?? null;
		}
		else {
			return dateValue;
		}
	};

	useEffect(() => {
		if (value !== null && value !== undefined && !value.toString().startsWith('0001-01-01')) {
			setDateValue(dayjs(value));
		} else {
			setDateValue(null);
		}
	}, [value]);

	useEffect(() => {
		if (cleared) {
			const timeout = setTimeout(() => {
				setCleared(false);
			}, 300);

			return () => clearTimeout(timeout);
		}
		return () => { };
	}, [cleared]);

	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (isExpanded && inputRef.current) {
			inputRef.current?.focus();
		}
	}, [isExpanded])

	const CustomDay = (props: PickersDayProps<Dayjs> & { highlightedDays?: string[] }) => {
		const { highlightedDays = [], day, outsideCurrentMonth, isLastVisibleCell, isFirstVisibleCell, onDaySelect } = props;
		let matchedStyles = {};

		if (highlightedDays) {
			matchedStyles = highlightedDays.reduce((a, v) => {
				if (day?.format('YYYY/MM/DD') === v) {
					return { border: "1px solid #004b95" };
				}
				return a;
			}, {});
		}

		return <PickersDay
			{...props}
			sx={{ ...matchedStyles }}
			day={day}
			onDaySelect={onDaySelect}
			isFirstVisibleCell={isFirstVisibleCell}
			isLastVisibleCell={isLastVisibleCell}
			outsideCurrentMonth={outsideCurrentMonth}
		/>;
	};

	return <Grid container item xs={xsFormControl} md={mdFormControl} sx={itemSx ?? {
		...paddingX1Props,
		alignContent: 'flex-start'
	}}>
		<FormControl
			fullWidth
			margin='normal'
			required={isRequired}
			error={formState?.errors[errorName ?? name] !== undefined}
			{...props}
		>
			<LocalizationProvider
				dateAdapter={AdapterDayjs}
			>
				<DemoContainer
					components={["DatePicker"]}
					sx={{
						width: "100%",
					}}
				>
					<Box
						// onFocus={() => setIsFocused(true)}
						// onBlur={() => setIsFocused(false)}
						sx={{ width: "100%" }}
						key={`${childKey}-form-control-box`}
					>
						<Grid container xs={12} flexWrap={'nowrap'} >
							<Grid item xs={isIndicatorTakeSpace?11:12}>
								<DatePicker
									name={name}
									label={label}
									value={handleValue(dateValue)}
									disableHighlightToday={true}
									onChange={(newValue) => {
										if (newValue?.isValid()) {
											onChange(newValue.format("YYYY-MM-DD"));
										}
										else {
											onChange(newValue?.toString());
										}
									}}
									slots={{
										day: CustomDay,
									}}
									slotProps={{
										field: { clearable: true, onClear: () => setCleared(true) },
										textField: {
											required: isRequired,
											inputRef: inputRef,
											error: formState?.errors[name] !== undefined,
										},
										popper: {
											sx: {
												zIndex: zIndex ?? 99999
											}
										},
										day: {
											highlightedDays: highlightedDays ?? []
										} as any,
									}}
									disabled={isDisabled}
									sx={{
										width: "100%",
									}}
								/>
							</Grid>
							{showIndicator !== undefined && showIndicator() == true &&
								<Grid item xs={isIndicatorTakeSpace?1:0} sx={{ ...flexCenterProps }}>
									<DiffInheritanceIndicator />
								</Grid>
							}
						</Grid>
					</Box>
				</DemoContainer>
			</LocalizationProvider>
			{formState && <FieldError formState={formState} name={errorName ?? name} textAlign={textErrorAlign} />}
		</FormControl>
	</Grid >;
};

export default memo(NewHighlightedDatePickerFormControl);
